import React from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/landing/Navbar";
import Footer from "../components/landing/Footer";
import { Button } from "../components/ui/button";
import SEOHead from "../components/SEOHead";
import {
  Recycle,
  Leaf,
  HeartHandshake,
  MapPin,
  ShieldCheck,
  ArrowRight,
  Sparkles,
} from "lucide-react";
import { MAIN_APP_URL } from "../lib/config";

const VALUES = [
  {
    icon: HeartHandshake,
    title: "Fair to everyone",
    body: "No hidden markups, no subscriptions. A flat 5% fee, the same for customer and carrier. Money flows where the work happens.",
  },
  {
    icon: ShieldCheck,
    title: "Lawful by default",
    body: "Carriers are checked against the official waste carrier register for their area before they can quote, and we report illegal disposal — that's how we cut fly-tipping at the source.",
  },
  {
    icon: Recycle,
    title: "Reuse first",
    body: "The cheapest, greenest waste is the waste that doesn't exist. Where an item can be reused, we help you find someone nearby who wants it.",
  },
  {
    icon: MapPin,
    title: "Local first",
    body: "Binshare is hyper-local by design. Local carriers earning more from tighter rounds, quicker collections for customers, cleaner residential roads.",
  },
];

const TIMELINE = [
  {
    year: "Late 2025",
    title: "Binshare is founded",
    body: "Binshare Ltd is incorporated in Caernarfon, Gwynedd (Company No. 16909547) to help tackle fly-tipping in the UK.",
  },
  {
    year: "Dec 2025",
    title: "Marketplace goes live",
    body: "The Binshare app launches — customers can post waste and book registered waste carriers from any device, on any browser.",
  },
  {
    year: "2026",
    title: "Growing from North Wales",
    body: "Now live in North Wales and the North West. We are starting in North Wales, Cheshire, the Wirral and Liverpool, and onboarding more registered waste carriers.",
  },
];

export default function AboutPage() {
  return (
    <div
      className="min-h-screen bg-cream-50 text-moss-900"
      data-testid="about-page"
      id="top"
    >
      <SEOHead
        path="/about"
        title="About Binshare | UK Waste Disposal Marketplace &amp; Skip Hire Alternative"
        description="Binshare is a UK waste marketplace connecting households and businesses with registered waste carriers. Based near Llanberis, North Wales."
        keywords="about Binshare, UK waste marketplace, skip hire alternative, registered waste carriers"
      />
      <Navbar />

      {/* Hero */}
      <section
        id="about"
        className="pt-32 md:pt-40 pb-16 md:pb-20 bg-gradient-to-b from-sage-50 via-cream-50 to-cream-50 relative overflow-hidden"
      >
        <div className="absolute -top-24 right-0 w-[420px] h-[420px] bg-sage-200/40 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 -left-32 w-[420px] h-[420px] bg-moss-200/30 rounded-full blur-3xl pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-moss-900 text-cream-50 rounded-full text-xs font-semibold tracking-[0.14em] uppercase">
            <Sparkles className="w-3.5 h-3.5" /> About Binshare
          </div>
          <h1 className="font-display mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] text-moss-950 text-balance max-w-4xl">
            Making waste disposal{" "}
            <span className="text-sage-600">affordable</span> again — and
            cleaning up Britain in the process.
          </h1>
          <p className="mt-6 text-base md:text-lg text-moss-700 max-w-3xl leading-relaxed">
            Binshare is a UK-built waste marketplace. We connect customers
            with registered waste carriers so households and businesses can
            get things collected quickly, legally and at a fair price.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <a
              href={MAIN_APP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="about-post-job"
            >
              <Button className="h-12 px-7 rounded-full bg-moss-900 hover:bg-moss-950 text-cream-50">
                Post a job
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </a>
            <Link to="/" data-testid="about-back-to-landing">
              <Button
                variant="outline"
                className="h-12 px-7 rounded-full bg-transparent border-2 border-moss-900 text-moss-900 hover:bg-moss-900 hover:text-cream-50"
              >
                Back to home
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section
        id="mission"
        className="py-20 md:py-24 bg-cream-50"
        data-testid="about-mission"
      >
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-sage-100 text-sage-800 rounded-full text-xs font-semibold tracking-[0.14em] uppercase">
              <Leaf className="w-3.5 h-3.5" /> Our mission
            </div>
            <h2 className="font-display mt-5 text-3xl sm:text-4xl lg:text-5xl font-bold text-moss-950 leading-[1.08]">
              A movement for change — one collection at a time.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-5 text-moss-700 text-base md:text-lg leading-relaxed">
            <p>
              The UK throws away a huge amount of waste every year, and
              councils and landowners pay to{" "}
              <strong className="text-moss-950">
                clean up fly-tipping
              </strong>{" "}
              that often happens because legal disposal feels expensive or
              inaccessible.
            </p>
            <p>
              Binshare&apos;s mission is to fix that — connecting the people
              who have waste with the registered waste carriers who can move it,
              through a simple app and a fair, transparent marketplace built
              for the UK and operated from the UK.
            </p>
            <p>
              We&apos;re not trying to replace councils or existing commercial
              contracts. We&apos;re filling the gap they leave behind, so
              fewer fridges end up in lay-bys and fewer people pay
              over-the-odds to clear a garden or empty a garage.
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 md:py-24 bg-gradient-to-br from-sage-50 via-cream-50 to-cream-100">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-moss-900 text-cream-50 rounded-full text-xs font-semibold tracking-[0.14em] uppercase">
              What we stand for
            </div>
            <h2 className="font-display mt-5 text-3xl sm:text-4xl lg:text-5xl font-bold text-moss-950 leading-tight">
              Four principles that shape every product decision.
            </h2>
          </div>

          <div className="mt-12 grid sm:grid-cols-2 gap-5 lg:gap-6">
            {VALUES.map((v) => {
              const Icon = v.icon;
              return (
                <div
                  key={v.title}
                  className="bg-white rounded-3xl p-7 border border-moss-100 hover:shadow-lg transition-shadow"
                  data-testid={`about-value-${v.title
                    .toLowerCase()
                    .replace(/\s+/g, "-")}`}
                >
                  <div className="w-12 h-12 rounded-xl bg-sage-500 flex items-center justify-center mb-5">
                    <Icon className="w-6 h-6 text-cream-50" />
                  </div>
                  <h3 className="font-display text-2xl font-bold text-moss-950 mb-2">
                    {v.title}
                  </h3>
                  <p className="text-moss-700 leading-relaxed">{v.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Story / timeline */}
      <section className="py-20 md:py-24 bg-cream-50">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-sage-100 text-sage-800 rounded-full text-xs font-semibold tracking-[0.14em] uppercase">
              Our story
            </div>
            <h2 className="font-display mt-5 text-3xl sm:text-4xl lg:text-5xl font-bold text-moss-950 leading-tight">
              Built in the UK, for the UK.
            </h2>
          </div>

          <ol className="mt-12 relative border-l-2 border-moss-200/60 ml-2 space-y-10">
            {TIMELINE.map((t) => (
              <li key={t.title} className="pl-7 relative">
                <span className="absolute -left-[11px] top-1.5 w-5 h-5 rounded-full bg-moss-900 border-4 border-cream-50" />
                <p className="text-xs font-semibold tracking-[0.16em] uppercase text-sage-700">
                  {t.year}
                </p>
                <h3 className="font-display text-xl sm:text-2xl font-bold text-moss-950 mt-1">
                  {t.title}
                </h3>
                <p className="text-moss-700 leading-relaxed mt-2 max-w-2xl">
                  {t.body}
                </p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Company info */}
      <section className="py-16 md:py-20 bg-moss-950 text-cream-50">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-cream-50/10 text-cream-50 rounded-full text-xs font-semibold tracking-[0.14em] uppercase border border-cream-50/20">
              Company information
            </div>
            <h2 className="font-display mt-5 text-3xl sm:text-4xl font-bold leading-tight">
              Binshare Ltd
            </h2>
            <p className="mt-4 text-cream-100/80 leading-relaxed">
              Registered in England &amp; Wales · Company No.{" "}
              <strong className="text-cream-50">16909547</strong>. Registered
              office: Caernarfon, Gwynedd, United Kingdom.
            </p>
            <p className="mt-4 text-cream-100/70 text-sm">
              Proudly Welsh. Based near Llanberis, North Wales.
            </p>
          </div>
          <div className="lg:col-span-7 grid gap-4">
            <ContactCard role="Get in touch" email="support@binshare.co.uk" />
            <p className="text-xs text-cream-100/60 leading-relaxed">
              One inbox, one team. Whether it&apos;s a general question, a
              privacy request, a trust &amp; safety report or a press enquiry
              — email support and we&apos;ll route it to the right place.
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function ContactCard({ role, email }) {
  return (
    <div className="rounded-2xl border border-cream-50/15 bg-moss-900/50 p-5">
      <p className="text-[10px] font-semibold tracking-[0.16em] uppercase text-cream-100/60">
        {role}
      </p>
      <a
        href={`mailto:${email}`}
        className="font-display text-base sm:text-lg font-semibold text-cream-50 mt-1.5 inline-flex items-center gap-1.5 hover:text-sage-300 transition-colors"
      >
        {email}
        <ArrowRight className="w-3.5 h-3.5" />
      </a>
    </div>
  );
}
